'use client';

import { c as _c } from "react/compiler-runtime";
import type { MouseEvent } from 'react';
import { COLORS, MULTIPLE_COLORS } from '@/constants/ui.constants';
import { ChartTooltipPanel, FloatingTooltip } from '@/domains/pages/shared/chart-tooltip';
import { TEAM_COST_PER_TASK_BAR_HEIGHT_PX, TEAM_COST_PER_TASK_BAR_PLOT_HEIGHT_PX, TEAM_COST_PER_TASK_ORG_AVERAGE_TOOLTIP, TEAM_COST_PER_TASK_REFERENCE_LABEL_HEIGHT_PX, TEAM_COST_PER_TASK_TOP_QUARTILE_TOOLTIP } from '@/domains/pages/ai-spend/constants';
import type { TeamSpendSnapshot } from '@/domains/pages/ai-spend/utils/map-ai-spend-aggregates';
import { barFillGradient } from '@/domains/pages/ai-spend/utils/bar-fill';
import { TooltipButton } from '@/comps/tooltip-button';
import { Info } from 'lucide-react';
import { formatSpend } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { useCallback, useMemo, useState } from 'react';
type TeamCostPerTaskBarsProps = {
  teams: TeamSpendSnapshot[];
  colorThreshold: number | null;
  orgAverage: number | null;
  hoveredKey: string | null;
  onHoverChange: (key: string | null) => void;
  className?: string;
};
function referenceLineBottomPx(value: number | null, maxValue: number, chartHeight: number): number | null {
  if (value == null || !Number.isFinite(value) || maxValue <= 0) return null;
  const bottom = value / maxValue * chartHeight;
  return Math.min(bottom, chartHeight - 1);
}
function barColorForTeam(spendPerTask: number | null, threshold: number | null): string {
  if (spendPerTask == null || threshold == null) return COLORS.ACCENT;
  return spendPerTask <= threshold ? MULTIPLE_COLORS[3] : MULTIPLE_COLORS[2];
}
export type ReferenceLabelPlacement = 'above' | 'below';
const REFERENCE_LABEL_GAP_PX = 4;
const REFERENCE_LABEL_COLLISION_THRESHOLD_PX = 16;
export function referenceLineTopPx(bottomPx: number, plotHeight: number): number {
  return plotHeight - bottomPx;
}
export function referenceLabelTopPx(bottomPx: number, placement: ReferenceLabelPlacement, plotHeight: number = TEAM_COST_PER_TASK_BAR_PLOT_HEIGHT_PX): number {
  const lineTop = referenceLineTopPx(bottomPx, plotHeight);
  if (placement === 'above') {
    return lineTop - REFERENCE_LABEL_GAP_PX;
  }
  return lineTop + REFERENCE_LABEL_GAP_PX;
}
function referenceLabelPlacementFits(bottomPx: number, placement: ReferenceLabelPlacement, plotHeight: number): boolean {
  const lineTop = referenceLineTopPx(bottomPx, plotHeight);
  if (placement === 'above') {
    const labelTop = lineTop - REFERENCE_LABEL_GAP_PX - TEAM_COST_PER_TASK_REFERENCE_LABEL_HEIGHT_PX;
    return labelTop >= -TEAM_COST_PER_TASK_REFERENCE_LABEL_HEIGHT_PX;
  }
  return lineTop + REFERENCE_LABEL_GAP_PX + TEAM_COST_PER_TASK_REFERENCE_LABEL_HEIGHT_PX <= plotHeight;
}
function resolveReferenceLabelPlacement(bottomPx: number, preferred: ReferenceLabelPlacement, plotHeight: number): ReferenceLabelPlacement {
  if (referenceLabelPlacementFits(bottomPx, preferred, plotHeight)) return preferred;
  const alternate = preferred === 'above' ? 'below' : 'above';
  if (referenceLabelPlacementFits(bottomPx, alternate, plotHeight)) return alternate;
  return preferred;
}

/** Labels sit above or below their line with a fixed gap; stagger only when lines are close. */
export function resolveReferenceLabelPlacements(topQuartileBottomPx: number | null, orgAverageBottomPx: number | null, plotHeight: number = TEAM_COST_PER_TASK_BAR_PLOT_HEIGHT_PX): {
  topQuartile: ReferenceLabelPlacement;
  orgAverage: ReferenceLabelPlacement;
} {
  if (topQuartileBottomPx != null && orgAverageBottomPx != null) {
    const linesAreClose = Math.abs(topQuartileBottomPx - orgAverageBottomPx) < REFERENCE_LABEL_COLLISION_THRESHOLD_PX;
    if (linesAreClose) {
      const topQuartileIsHigher = topQuartileBottomPx >= orgAverageBottomPx;
      return {
        topQuartile: resolveReferenceLabelPlacement(topQuartileBottomPx, topQuartileIsHigher ? 'above' : 'below', plotHeight),
        orgAverage: resolveReferenceLabelPlacement(orgAverageBottomPx, topQuartileIsHigher ? 'below' : 'above', plotHeight)
      };
    }
    return {
      topQuartile: resolveReferenceLabelPlacement(topQuartileBottomPx, 'above', plotHeight),
      orgAverage: resolveReferenceLabelPlacement(orgAverageBottomPx, 'above', plotHeight)
    };
  }
  if (topQuartileBottomPx != null) {
    return {
      topQuartile: resolveReferenceLabelPlacement(topQuartileBottomPx, 'above', plotHeight),
      orgAverage: 'below'
    };
  }
  if (orgAverageBottomPx != null) {
    return {
      topQuartile: 'above',
      orgAverage: resolveReferenceLabelPlacement(orgAverageBottomPx, 'above', plotHeight)
    };
  }
  return {
    topQuartile: 'above',
    orgAverage: 'below'
  };
}
function ReferenceLineGuide(t0) {
  const $ = _c(2);
  const {
    bottomPx
  } = t0;
  const t1 = `${bottomPx}px`;
  let t2;
  if ($[0] !== t1) {
    t2 = <div aria-hidden={true} className="pointer-events-none absolute inset-x-0 border-t border-dashed border-muted-foreground" style={{
      bottom: t1
    }} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}
function ReferenceLineLabel(t0) {
  const $ = _c(20);
  const {
    bottomPx,
    label,
    tooltip,
    placement,
    plotHeight
  } = t0;
  let t1;
  if ($[0] !== bottomPx || $[1] !== placement || $[2] !== plotHeight) {
    t1 = referenceLabelTopPx(bottomPx, placement, plotHeight);
    $[0] = bottomPx;
    $[1] = placement;
    $[2] = plotHeight;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const topPx = t1;
  const t2 = placement === "above" && "-translate-y-full";
  let t3;
  if ($[4] !== t2) {
    t3 = cn("pointer-events-auto absolute right-0 z-30 inline-flex items-center gap-1 text-[10px] leading-none text-muted-foreground hover:text-foreground", t2);
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = `${topPx}px`;
  let t5;
  if ($[6] !== t4) {
    t5 = {
      top: t4
    };
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = `${label} description`;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Info className="size-3" strokeWidth={2} />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t6) {
    t8 = <button type="button" className="cursor-default p-0 text-inherit outline-hidden focus-visible:ring-2 focus-visible:ring-ring" aria-label={t6}>{t7}</button>;
    $[9] = t6;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== label || $[12] !== placement || $[13] !== t3 || $[14] !== t5 || $[15] !== t8) {
    t9 = <span className={t3} style={t5} data-label-placement={placement}>{label}{t8}</span>;
    $[11] = label;
    $[12] = placement;
    $[13] = t3;
    $[14] = t5;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t9 || $[18] !== tooltip) {
    t10 = <TooltipButton content={tooltip}>{t9}</TooltipButton>;
    $[17] = t9;
    $[18] = tooltip;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
}
export function getTeamCostPerTaskBarColor(spendPerTask: number | null, threshold: number | null): string {
  return barColorForTeam(spendPerTask, threshold);
}
export function TeamCostPerTaskBars(t0) {
  const $ = _c(88);
  const {
    teams,
    colorThreshold,
    orgAverage,
    hoveredKey,
    onHoverChange,
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      x: 0,
      y: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [tooltipPos, setTooltipPos] = useState(t1);
  let T0;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[1] !== className || $[2] !== colorThreshold || $[3] !== hoveredKey || $[4] !== onHoverChange || $[5] !== orgAverage || $[6] !== teams || $[7] !== tooltipPos) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const hoveredTeam = teams.find(team => team.key === hoveredKey) ?? null;
      let t10;
      if ($[17] !== colorThreshold || $[18] !== orgAverage || $[19] !== teams) {
        const values = teams.map(_temp).filter(_temp2);
        if (colorThreshold != null) {
          values.push(colorThreshold);
        }
        if (orgAverage != null && Number.isFinite(orgAverage)) {
          values.push(orgAverage);
        }
        t10 = values.length > 0 ? Math.max(...values) : 0;
        $[17] = colorThreshold;
        $[18] = orgAverage;
        $[19] = teams;
        $[20] = t10;
      } else {
        t10 = $[20];
      }
      const maxSpendPerTask = t10;
      let t11;
      if ($[21] !== colorThreshold || $[22] !== maxSpendPerTask) {
        t11 = referenceLineBottomPx(colorThreshold, maxSpendPerTask, TEAM_COST_PER_TASK_BAR_PLOT_HEIGHT_PX);
        $[21] = colorThreshold;
        $[22] = maxSpendPerTask;
        $[23] = t11;
      } else {
        t11 = $[23];
      }
      const topQuartileLineBottomPx = t11;
      let t12;
      if ($[24] !== maxSpendPerTask || $[25] !== orgAverage) {
        t12 = referenceLineBottomPx(orgAverage, maxSpendPerTask, TEAM_COST_PER_TASK_BAR_PLOT_HEIGHT_PX);
        $[24] = maxSpendPerTask;
        $[25] = orgAverage;
        $[26] = t12;
      } else {
        t12 = $[26];
      }
      const orgAverageLineBottomPx = t12;
      let t13;
      if ($[27] !== orgAverageLineBottomPx || $[28] !== topQuartileLineBottomPx) {
        t13 = resolveReferenceLabelPlacements(topQuartileLineBottomPx, orgAverageLineBottomPx);
        $[27] = orgAverageLineBottomPx;
        $[28] = topQuartileLineBottomPx;
        $[29] = t13;
      } else {
        t13 = $[29];
      }
      const labelPlacements = t13;
      let t14;
      if ($[30] !== onHoverChange) {
        t14 = (event, team_1) => {
          onHoverChange(team_1.key);
          setTooltipPos({
            x: event.clientX,
            y: event.clientY
          });
        };
        $[30] = onHoverChange;
        $[31] = t14;
      } else {
        t14 = $[31];
      }
      const showBar = t14;
      let t15;
      if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
        t15 = event_0 => {
          setTooltipPos({
            x: event_0.clientX,
            y: event_0.clientY
          });
        };
        $[32] = t15;
      } else {
        t15 = $[32];
      }
      const moveBar = t15;
      let t16;
      if ($[33] !== onHoverChange) {
        t16 = () => {
          onHoverChange(null);
        };
        $[33] = onHoverChange;
        $[34] = t16;
      } else {
        t16 = $[34];
      }
      const hideBar = t16;
      if (teams.length === 0) {
        let t17;
        if ($[35] !== className) {
          t17 = cn("flex items-center justify-center text-sm text-muted-foreground", className);
          $[35] = className;
          $[36] = t17;
        } else {
          t17 = $[36];
        }
        let t18;
        if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
          t18 = {
            height: `${TEAM_COST_PER_TASK_BAR_HEIGHT_PX}px`
          };
          $[37] = t18;
        } else {
          t18 = $[37];
        }
        let t19;
        if ($[38] !== t17) {
          t19 = <div className={t17} style={t18} data-testid="team-cost-per-task-bars">No team task data yet</div>;
          $[38] = t17;
          $[39] = t19;
        } else {
          t19 = $[39];
        }
        t9 = t19;
        break bb0;
      }
      if ($[40] !== className) {
        t6 = cn("relative", className);
        $[40] = className;
        $[41] = t6;
      } else {
        t6 = $[41];
      }
      t7 = "team-cost-per-task-bars";
      let t17;
      if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = {
          height: `${TEAM_COST_PER_TASK_BAR_HEIGHT_PX}px`
        };
        $[42] = t17;
      } else {
        t17 = $[42];
      }
      let t18;
      if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
        t18 = {
          height: `${TEAM_COST_PER_TASK_BAR_PLOT_HEIGHT_PX}px`
        };
        $[43] = t18;
      } else {
        t18 = $[43];
      }
      let t19;
      if ($[44] !== colorThreshold || $[45] !== hoveredKey || $[46] !== maxSpendPerTask || $[47] !== showBar || $[48] !== teams) {
        let t20;
        if ($[50] !== colorThreshold || $[51] !== hoveredKey || $[52] !== maxSpendPerTask || $[53] !== showBar) {
          t20 = team_2 => {
            const spendPerTask = team_2.spendPerTask ?? 0;
            const heightRatio = maxSpendPerTask > 0 ? spendPerTask / maxSpendPerTask : 0;
            const barHeight = Math.max(4, heightRatio * TEAM_COST_PER_TASK_BAR_PLOT_HEIGHT_PX);
            const isDimmed = hoveredKey != null && hoveredKey !== team_2.key;
            const barColor = barColorForTeam(team_2.spendPerTask, colorThreshold);
            return <div key={team_2.key} className={cn("flex min-w-0 flex-1 flex-col items-center justify-end transition-opacity", isDimmed && "opacity-35")} onMouseEnter={event_1 => showBar(event_1, team_2)} onMouseMove={moveBar}><div className="w-full cursor-pointer rounded-t-[3px]" style={{
                height: `${barHeight}px`,
                background: barFillGradient(barColor, "vertical"),
                borderTop: `1px solid ${barColor}`,
                borderLeft: `1px solid ${barColor}`,
                borderRight: `1px solid ${barColor}`,
                borderBottom: "none"
              }} /></div>;
          };
          $[50] = colorThreshold;
          $[51] = hoveredKey;
          $[52] = maxSpendPerTask;
          $[53] = showBar;
          $[54] = t20;
        } else {
          t20 = $[54];
        }
        t19 = teams.map(t20);
        $[44] = colorThreshold;
        $[45] = hoveredKey;
        $[46] = maxSpendPerTask;
        $[47] = showBar;
        $[48] = teams;
        $[49] = t19;
      } else {
        t19 = $[49];
      }
      let t20;
      if ($[55] !== t19) {
        t20 = <div className="absolute inset-x-0 bottom-0 flex w-full items-end gap-1.5" style={t18}>{t19}</div>;
        $[55] = t19;
        $[56] = t20;
      } else {
        t20 = $[56];
      }
      let t21;
      if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
        t21 = {
          height: `${TEAM_COST_PER_TASK_BAR_PLOT_HEIGHT_PX}px`
        };
        $[57] = t21;
      } else {
        t21 = $[57];
      }
      let t22;
      if ($[58] !== topQuartileLineBottomPx) {
        t22 = topQuartileLineBottomPx != null ? <ReferenceLineGuide bottomPx={topQuartileLineBottomPx} /> : null;
        $[58] = topQuartileLineBottomPx;
        $[59] = t22;
      } else {
        t22 = $[59];
      }
      let t23;
      if ($[60] !== orgAverageLineBottomPx) {
        t23 = orgAverageLineBottomPx != null ? <ReferenceLineGuide bottomPx={orgAverageLineBottomPx} /> : null;
        $[60] = orgAverageLineBottomPx;
        $[61] = t23;
      } else {
        t23 = $[61];
      }
      let t24;
      if ($[62] !== labelPlacements || $[63] !== topQuartileLineBottomPx) {
        t24 = topQuartileLineBottomPx != null ? <ReferenceLineLabel bottomPx={topQuartileLineBottomPx} label="Top quartile" tooltip={TEAM_COST_PER_TASK_TOP_QUARTILE_TOOLTIP} placement={labelPlacements.topQuartile} plotHeight={TEAM_COST_PER_TASK_BAR_PLOT_HEIGHT_PX} /> : null;
        $[62] = labelPlacements;
        $[63] = topQuartileLineBottomPx;
        $[64] = t24;
      } else {
        t24 = $[64];
      }
      let t25;
      if ($[65] !== labelPlacements || $[66] !== orgAverageLineBottomPx) {
        t25 = orgAverageLineBottomPx != null ? <ReferenceLineLabel bottomPx={orgAverageLineBottomPx} label="Avg. per task" tooltip={TEAM_COST_PER_TASK_ORG_AVERAGE_TOOLTIP} placement={labelPlacements.orgAverage} plotHeight={TEAM_COST_PER_TASK_BAR_PLOT_HEIGHT_PX} /> : null;
        $[65] = labelPlacements;
        $[66] = orgAverageLineBottomPx;
        $[67] = t25;
      } else {
        t25 = $[67];
      }
      let t26;
      if ($[68] !== t22 || $[69] !== t23 || $[70] !== t24 || $[71] !== t25) {
        t26 = <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 overflow-y-visible" style={t21}>{t22}{t23}{t24}{t25}</div>;
        $[68] = t22;
        $[69] = t23;
        $[70] = t24;
        $[71] = t25;
        $[72] = t26;
      } else {
        t26 = $[72];
      }
      if ($[73] !== hideBar || $[74] !== t20 || $[75] !== t26) {
        t8 = <div className="relative overflow-x-hidden" style={t17} onMouseLeave={hideBar}>{t20}{t26}</div>;
        $[73] = hideBar;
        $[74] = t20;
        $[75] = t26;
        $[76] = t8;
      } else {
        t8 = $[76];
      }
      T0 = FloatingTooltip;
      t2 = hoveredTeam != null;
      t3 = tooltipPos.x;
      t4 = tooltipPos.y;
      t5 = hoveredTeam ? <ChartTooltipPanel title={hoveredTeam.name} rows={[{
        label: "Cost per task",
        value: formatSpend(hoveredTeam.spendPerTask),
        valueStyle: {
          color: barColorForTeam(hoveredTeam.spendPerTask, colorThreshold)
        }
      }]} /> : null;
    }
    $[1] = className;
    $[2] = colorThreshold;
    $[3] = hoveredKey;
    $[4] = onHoverChange;
    $[5] = orgAverage;
    $[6] = teams;
    $[7] = tooltipPos;
    $[8] = T0;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    T0 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
    t9 = $[16];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  let t10;
  if ($[77] !== T0 || $[78] !== t2 || $[79] !== t3 || $[80] !== t4 || $[81] !== t5) {
    t10 = <T0 open={t2} x={t3} y={t4}>{t5}</T0>;
    $[77] = T0;
    $[78] = t2;
    $[79] = t3;
    $[80] = t4;
    $[81] = t5;
    $[82] = t10;
  } else {
    t10 = $[82];
  }
  let t11;
  if ($[83] !== t10 || $[84] !== t6 || $[85] !== t7 || $[86] !== t8) {
    t11 = <div className={t6} data-testid={t7}>{t8}{t10}</div>;
    $[83] = t10;
    $[84] = t6;
    $[85] = t7;
    $[86] = t8;
    $[87] = t11;
  } else {
    t11 = $[87];
  }
  return t11;
}
function _temp2(value) {
  return value != null && Number.isFinite(value);
}
function _temp(team_0) {
  return team_0.spendPerTask;
}